import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi, formatApiErrorDetail } from "./adminApi";

const TYPE_LABEL = {
    demo: "Demo",
    friends_family: "Friends & Family",
};

export default function AdminAccessCodes() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminApi.accessCodes();
            setItems(r.items || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const toggleActive = async (code, active) => {
        try {
            await adminApi.updateAccessCode(code, { active: !active });
            toast.success(active ? "Code disabled" : "Code enabled");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const del = async (code) => {
        if (!window.confirm(`Delete code "${code}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteAccessCode(code);
            toast.success("Code deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    return (
        <div className="px-8 py-10" data-testid="admin-access-codes">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">Operator</div>
                    <h1 className="mt-1 font-display text-3xl text-burgundy">
                        <span className="italic">Access</span> codes
                    </h1>
                    <p className="mt-2 text-sm text-ink-muted max-w-2xl">
                        Codes let friends, family, and demo users unlock the full experience without payment. Users enter codes via the "I have an access code" link on the Paywall.
                    </p>
                </div>
                <button
                    data-testid="ac-create-btn"
                    onClick={() => setShowCreate(true)}
                    className="bg-burgundy text-cream uppercase text-[10px] tracking-[0.24em] px-5 py-3 hover:bg-burgundy-deep"
                >
                    + New code
                </button>
            </div>

            <div className="border border-burgundy/15 bg-cream overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-cream-soft text-[10px] tracking-[0.28em] uppercase text-ink-muted">
                        <tr>
                            <th className="text-left px-4 py-3">Code</th>
                            <th className="text-left px-4 py-3">Label</th>
                            <th className="text-left px-4 py-3">Type</th>
                            <th className="text-left px-4 py-3">Uses</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-muted">Loading…</td></tr>
                        )}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-muted">No codes yet.</td></tr>
                        )}
                        {!loading && items.map(c => {
                            const total = c.max_uses;
                            const remaining = total === null || total === undefined ? "∞" : c.uses_left;
                            const used = c.uses_count || 0;
                            return (
                                <tr key={c.code} className="border-t border-burgundy/10" data-testid={`ac-row-${c.code}`}>
                                    <td className="px-4 py-3 font-mono text-burgundy">{c.code}</td>
                                    <td className="px-4 py-3 text-ink">{c.label || "—"}</td>
                                    <td className="px-4 py-3 text-ink-muted">{TYPE_LABEL[c.type] || c.type}</td>
                                    <td className="px-4 py-3 text-ink">
                                        {used} used · {remaining} left
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] tracking-[0.28em] uppercase px-2 py-1 ${c.active ? "bg-burgundy/[0.08] text-burgundy" : "bg-ink-muted/15 text-ink-muted"}`}>
                                            {c.active ? "Active" : "Disabled"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-3">
                                        <button
                                            data-testid={`ac-toggle-${c.code}`}
                                            onClick={() => toggleActive(c.code, c.active)}
                                            className="text-[10px] tracking-[0.24em] uppercase text-burgundy hover:underline"
                                        >
                                            {c.active ? "Disable" : "Enable"}
                                        </button>
                                        <button
                                            data-testid={`ac-delete-${c.code}`}
                                            onClick={() => del(c.code)}
                                            className="text-[10px] tracking-[0.24em] uppercase text-burgundy/70 hover:text-burgundy hover:underline"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreate && (
                <CreateModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); load(); }}
                />
            )}
        </div>
    );
}

function CreateModal({ onClose, onCreated }) {
    const [code, setCode] = useState("");
    const [label, setLabel] = useState("");
    const [type, setType] = useState("friends_family");
    const [maxUses, setMaxUses] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const submit = async () => {
        const c = code.trim();
        if (!c) { setError("Code is required"); return; }
        setSaving(true);
        setError(null);
        try {
            await adminApi.createAccessCode({
                code: c,
                label: label.trim() || null,
                type,
                max_uses: maxUses === "" ? null : parseInt(maxUses, 10),
                active: true,
            });
            toast.success("Code created");
            onCreated();
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center px-3" onClick={onClose} data-testid="ac-create-modal">
            <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-cream border border-burgundy/20">
                <div className="px-6 pt-6 pb-3 border-b border-burgundy/15">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">New code</div>
                    <h3 className="mt-1 font-display text-2xl text-burgundy">
                        <span className="italic">Create</span> an access code
                    </h3>
                </div>
                <div className="px-6 py-4 space-y-3">
                    <Field label="Code">
                        <input
                            data-testid="ac-create-code"
                            type="text"
                            autoFocus
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            placeholder="EVERAFTER-XYZ"
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-2.5 px-3 font-mono uppercase tracking-widest text-burgundy focus:outline-none"
                        />
                    </Field>
                    <Field label="Label (optional)">
                        <input
                            data-testid="ac-create-label"
                            type="text"
                            value={label}
                            onChange={e => setLabel(e.target.value)}
                            placeholder="e.g. Q1 launch event"
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-2.5 px-3 text-ink focus:outline-none"
                        />
                    </Field>
                    <Field label="Type">
                        <div className="flex gap-2" data-testid="ac-create-type">
                            {[
                                { v: "friends_family", l: "Friends & Family" },
                                { v: "demo", l: "Demo" },
                            ].map(opt => (
                                <button
                                    key={opt.v}
                                    onClick={() => setType(opt.v)}
                                    data-testid={`ac-type-${opt.v}`}
                                    className={`flex-1 py-2 text-[11px] tracking-[0.22em] uppercase border transition-colors ${
                                        type === opt.v ? "bg-burgundy text-cream border-burgundy" : "border-burgundy/20 text-ink"
                                    }`}
                                >
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    </Field>
                    <Field label="Max uses (blank = unlimited)">
                        <input
                            data-testid="ac-create-max"
                            type="number"
                            min={1}
                            value={maxUses}
                            onChange={e => setMaxUses(e.target.value)}
                            placeholder="e.g. 25"
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-2.5 px-3 text-ink focus:outline-none"
                        />
                    </Field>
                    {error && (
                        <div data-testid="ac-create-error" className="text-xs text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">
                            {error}
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-burgundy/15 flex gap-2">
                    <button onClick={onClose} className="flex-1 border border-burgundy/30 text-burgundy uppercase text-[11px] tracking-[0.22em] py-3">Cancel</button>
                    <button
                        data-testid="ac-create-submit"
                        onClick={submit}
                        disabled={saving || !code.trim()}
                        className="flex-1 bg-burgundy text-cream uppercase text-[11px] tracking-[0.22em] py-3 hover:bg-burgundy-deep disabled:opacity-50"
                    >
                        {saving ? "Creating…" : "Create code"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">{label}</div>
            {children}
        </div>
    );
}
